// 3D dice (issue 1183): the pure half. Decoration only — the result is ALWAYS the server's roll, read from the frame
// this viewer was sent; nothing here throws anything. Each die is a real polyhedron (d4 tetrahedron, d6 cube, d8
// octahedron, d10 pentagonal trapezohedron, d12 dodecahedron, d20 icosahedron) with a number on every face. Nothing is
// simulated: for each die type the rotation that turns a given face to the viewer, upright, is worked out once from
// the geometry (`landing`), and the tumble is that rotation with a spin added in front of it that decays to nothing —
// so the last frame shows exactly the server's face, by construction. A d100 is two d10s: a tens die (00–90) and a
// units die (0–9), read together as 1–100 ("00" and "0" is 100), the way a table throws percentile dice.
import type { EventBody, ProjectedEvent, RollRecord } from '@dragons-table/vtt-shared';

export type V3 = [number, number, number];
/** A rotation as a row-major 3 × 3 matrix. */
export type M3 = [number, number, number, number, number, number, number, number, number];

export const DIE_TYPES = [4, 6, 8, 10, 12, 20] as const;
export type DieSides = (typeof DIE_TYPES)[number];

export interface Face { verts: number[]; normal: V3; centre: V3; up: V3; label: string }
export interface Solid { sides: DieSides; verts: V3[]; faces: Face[] }

const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k];
export const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (a: V3): V3 => mul(a, 1 / Math.hypot(a[0], a[1], a[2]));
export const apply = (m: M3, v: V3): V3 => [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2], m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];
export const mulM = (a: M3, b: M3): M3 => {
  const o = new Array(9).fill(0) as M3;
  for (let r = 0; r < 3; r += 1) for (let c = 0; c < 3; c += 1) o[r * 3 + c] = a[r * 3]! * b[c]! + a[r * 3 + 1]! * b[3 + c]! + a[r * 3 + 2]! * b[6 + c]!;
  return o;
};
/** A rotation of `angle` about the unit `axis` (Rodrigues). */
export function axisAngle(axis: V3, angle: number): M3 {
  const [x, y, z] = norm(axis); const c = Math.cos(angle); const s = Math.sin(angle); const t = 1 - c;
  return [t * x * x + c, t * x * y - s * z, t * x * z + s * y, t * x * y + s * z, t * y * y + c, t * y * z - s * x, t * x * z - s * y, t * y * z + s * x, t * z * z + c];
}

/** A face from its vertex indices: wound counter-clockwise seen from outside, its outward normal, centre and an upright direction. */
function faceOf(verts: V3[], idx: number[]): Omit<Face, 'label'> {
  const centre = mul(idx.reduce<V3>((s, i) => add(s, verts[i]!), [0, 0, 0]), 1 / idx.length);
  let normal = norm(centre);
  // Wind counter-clockwise about the outward normal.
  const ref = norm(sub(verts[idx[0]!]!, centre)); const ref2 = cross(normal, ref);
  const sorted = [...idx].sort((a, b) => { const pa = sub(verts[a]!, centre); const pb = sub(verts[b]!, centre); return Math.atan2(dot(pa, ref2), dot(pa, ref)) - Math.atan2(dot(pb, ref2), dot(pb, ref)); });
  // A kite (the d10) is not centred on its own normal: the plane's normal is the true one.
  if (idx.length === 4) {
    const n = norm(cross(sub(verts[sorted[1]!]!, verts[sorted[0]!]!), sub(verts[sorted[2]!]!, verts[sorted[0]!]!)));
    normal = dot(n, centre) < 0 ? mul(n, -1) : n;
  }
  // Upright: toward the face's first corner for a triangle or pentagon; along the kite's long axis for a d10.
  const toward = idx.length === 4 ? verts[sorted.reduce((best, i) => (Math.abs(verts[i]![2]) > Math.abs(verts[best]![2]) ? i : best), sorted[0]!)]! : verts[sorted[0]!]!;
  const raw = sub(toward, centre);
  const up = norm(sub(raw, mul(normal, dot(raw, normal))));
  return { verts: sorted, normal, centre, up };
}

const PHI = (1 + Math.sqrt(5)) / 2;

function build(sides: DieSides): Solid {
  let verts: V3[] = []; let faces: number[][] = [];
  if (sides === 4) {
    verts = [[1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1]];
    faces = [[1, 2, 3], [0, 2, 3], [0, 1, 3], [0, 1, 2]];
  } else if (sides === 6) {
    for (const x of [-1, 1]) for (const y of [-1, 1]) for (const z of [-1, 1]) verts.push([x, y, z]);
    for (let axis = 0; axis < 3; axis += 1) for (const s of [-1, 1]) faces.push(verts.map((v, i) => [v, i] as const).filter(([v]) => v[axis] === s).map(([, i]) => i));
  } else if (sides === 8) {
    verts = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
    for (const x of [0, 1]) for (const y of [2, 3]) for (const z of [4, 5]) faces.push([x, y, z]);
  } else if (sides === 20 || sides === 12) {
    const ico: V3[] = [];
    for (const a of [-1, 1]) for (const b of [-PHI, PHI]) { ico.push([0, a, b], [a, b, 0], [b, 0, a]); }
    if (sides === 20) {
      verts = ico;
      const near = (i: number, j: number) => Math.abs(Math.hypot(...sub(ico[i]!, ico[j]!)) - 2) < 1e-6;
      for (let i = 0; i < 12; i += 1) for (let j = i + 1; j < 12; j += 1) for (let k = j + 1; k < 12; k += 1) if (near(i, j) && near(j, k) && near(i, k)) faces.push([i, j, k]);
    } else {
      const g = 1 / PHI;
      for (const x of [-1, 1]) for (const y of [-1, 1]) for (const z of [-1, 1]) verts.push([x, y, z]);
      for (const a of [-1, 1]) for (const b of [-1, 1]) verts.push([0, b * PHI, a * g], [b * PHI, a * g, 0], [a * g, 0, b * PHI]);
      // Each face's normal is an icosahedron vertex: the five corners nearest it.
      for (const n of ico) {
        const by = verts.map((v, i) => [dot(v, n), i] as const).sort((p, q) => q[0] - p[0]);
        faces.push(by.slice(0, 5).map(([, i]) => i));
      }
    }
  } else {
    // A pentagonal trapezohedron: apexes on the axis, two rings of five at ±c, the apex height making each kite flat.
    const c = 0.1; const h = c * (1 + Math.cos(Math.PI / 5)) / (1 - Math.cos(Math.PI / 5));
    verts = [[0, 0, h], [0, 0, -h]];
    for (let k = 0; k < 5; k += 1) { const a = (k * 2 * Math.PI) / 5; const b = a + Math.PI / 5; verts.push([Math.cos(a), Math.sin(a), c], [Math.cos(b), Math.sin(b), -c]); }
    const U = (k: number) => 2 + 2 * (k % 5); const L = (k: number) => 3 + 2 * (k % 5);
    for (let k = 0; k < 5; k += 1) faces.push([0, U(k), L(k), U(k + 1)], [1, L(k), U(k + 1), L(k + 1)]);
  }
  // Numbers: opposite faces of a d6, d8, d12 and d20 sum to sides + 1, as on real dice; a d10's to 9 (0–9).
  const built = faces.map((f) => faceOf(verts, f));
  const labels: string[] = new Array(built.length).fill('');
  const used = new Set<number>(); let next = 0;
  const opposite = (i: number) => built.findIndex((f, j) => j !== i && dot(f.normal, built[i]!.normal) < -0.999);
  for (let i = 0; i < built.length; i += 1) {
    if (used.has(i)) continue;
    const j = sides === 4 ? -1 : opposite(i);
    const lo = next; next += 1;
    if (sides === 10) { labels[i] = String(lo); if (j >= 0) { labels[j] = String(9 - lo); used.add(j); } }
    else { labels[i] = String(lo + 1); if (j >= 0) { labels[j] = String(sides - lo); used.add(j); } }
    used.add(i);
  }
  // Scale every die to a unit circumradius.
  const r = Math.max(...verts.map((v) => Math.hypot(...v)));
  const scaled = verts.map((v) => mul(v, 1 / r));
  return { sides, verts: scaled, faces: built.map((f, i) => ({ ...f, centre: mul(f.centre, 1 / r), label: labels[i]! })) };
}

export const SOLIDS: Record<DieSides, Solid> = Object.fromEntries(DIE_TYPES.map((s) => [s, build(s)])) as Record<DieSides, Solid>;

/**
 * The landing table: for each die type and each face's label, the rotation that turns that face to the viewer (+z)
 * with its number upright (+y). Worked out once from the geometry; `landing.test.ts` holds every entry.
 */
export const LANDING: Record<DieSides, Record<string, M3>> = Object.fromEntries(DIE_TYPES.map((s) => [s, Object.fromEntries(SOLIDS[s].faces.map((f) => {
  const x = cross(f.up, f.normal);
  return [f.label, [...x, ...f.up, ...f.normal] as M3];
}))])) as Record<DieSides, Record<string, M3>>;

/** Which face of a die, in an orientation, looks at the viewer: the one whose normal points most along +z. */
export function faceUp(sides: DieSides, m: M3): string {
  let best = ''; let z = -Infinity;
  for (const f of SOLIDS[sides].faces) { const n = apply(m, f.normal); if (n[2] > z) { z = n[2]; best = f.label; } }
  return best;
}

// ── What a roll shows ─────────────────────────────────────────────────────

/** One die to show: its type and the face that must land up. */
export interface ShownDie { sides: DieSides; face: string; tens?: true }
/** A roll to play: its id, the dice, and the result the log records (the dice's own reading, for the test's eyes). */
export interface DiceThrow { id: string; dice: ShownDie[]; faces: number[] }

/** The most dice one throw shows; a bigger pool shows its first this many (the log line has every one). */
export const MAX_SHOWN = 20;

/** A face as the dice show it: a d10 shows 0–9 ("10" is the 0), a d100 two d10s. Null when this die has no 3D shape. */
export function diceFor(sides: number, face: number): ShownDie[] | null {
  if (!Number.isInteger(face) || face < 1 || face > sides) return null;
  if (sides === 100) { const tens = face === 100 ? 0 : Math.floor(face / 10); const units = face % 10; return [{ sides: 10, face: String(tens), tens: true }, { sides: 10, face: String(units) }]; }
  if (sides === 10) return [{ sides: 10, face: String(face % 10) }];
  if ((DIE_TYPES as readonly number[]).includes(sides)) return [{ sides: sides as DieSides, face: String(face) }];
  return null;
}

/** What a d100's pair reads, or a plain die's face: the number the log records for it. */
export function readDice(dice: ShownDie[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < dice.length; i += 1) {
    const d = dice[i]!;
    if (d.tens) { const units = Number(dice[i + 1]!.face); const v = Number(d.face) * 10 + units; out.push(v === 0 ? 100 : v); i += 1; } else if (d.sides === 10) out.push(Number(d.face) === 0 ? 10 : Number(d.face)); else out.push(Number(d.face));
  }
  return out;
}

/** The dice of a roll record: every face thrown, each a die of its type — or null when this roll has no 3D dice. */
export function throwOf(roll: RollRecord): DiceThrow | null {
  // A template's saves (a creature each), an attack's chain and a rollable table's weights are not a dice-bag throw.
  if (roll.save || roll.attack || roll.table || roll.die.kind !== 'd') return null;
  const sides = roll.die.sides;
  const faces = roll.dice.filter((f): f is number => typeof f === 'number').slice(0, MAX_SHOWN);
  if (!faces.length) return null;
  const dice: ShownDie[] = [];
  for (const f of faces) { const d = diceFor(sides, f); if (!d) return null; dice.push(...d); }
  return { id: roll.id, dice, faces };
}

/** The throw a frame carries for this viewer — a roll they were sent — or null. Never anything they were not sent. */
export function throwFromFrame(ev: ProjectedEvent): DiceThrow | null {
  if (ev.kind === 'roll') return throwOf(ev.roll);
  if (ev.kind === 'dm.event' && (ev.body as EventBody).kind === 'roll') return throwOf((ev.body as Extract<EventBody, { kind: 'roll' }>).roll);
  return null;
}

// ── The tumble ────────────────────────────────────────────────────────────

export const TUMBLE_MS = 1300;
export const REST_MS = 1600;
export const FADE_MS = 400;

/** A small deterministic generator from a string, so a throw's tumble is the same however often it is drawn. */
export function seeded(key: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) { h ^= key.charCodeAt(i); h = Math.imul(h, 16777619); }
  return () => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) % 1_000_000) / 1_000_000; };
}

export interface Tumble { axis: V3; turns: number; from: { x: number; y: number }; to: { x: number; y: number } }

/** A die's pose at `t` (0–1 of the tumble): the landing rotation with a spin in front that decays to none, so t = 1 is exactly the landing. */
export function poseAt(sides: DieSides, face: string, tumble: Tumble, t: number): { m: M3; x: number; y: number; lift: number } {
  const e = 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);
  const land = LANDING[sides][face]!;
  const m = t >= 1 ? land : mulM(land, axisAngle(tumble.axis, tumble.turns * 2 * Math.PI * (1 - e)));
  const x = tumble.from.x + (tumble.to.x - tumble.from.x) * e;
  const y = tumble.from.y + (tumble.to.y - tumble.from.y) * e;
  // Two bounces, smaller each time.
  const lift = t >= 1 ? 0 : Math.abs(Math.sin(e * Math.PI * 2.5)) * (1 - e) * 0.35;
  return { m, x, y, lift };
}
